/**
 * Role Utilities
 * Provides role normalization and helper functions
 */

const ROLE_ALIASES = {
  team_leader: 'teamLeader',
  teamleader: 'teamLeader',
};

/**
 * Normalize role aliases without collapsing teamLeader into admin.
 * @param {string} role - User role
 * @returns {string} - Normalized role
 */
export const normalizeRole = (role) => {
  if (!role || typeof role !== 'string') return role;
  if (ROLE_ALIASES[role]) return ROLE_ALIASES[role];
  const lowered = role.toLowerCase();
  return ROLE_ALIASES[lowered] || role;
};

/**
 * Check if role is a real admin (not a team leader).
 * @param {string} role - User role
 * @returns {boolean}
 */
export const isAdmin = (role) => normalizeRole(role) === 'admin';

/**
 * Check if role is team leader.
 * @param {string} role - User role
 * @returns {boolean}
 */
export const isTeamLeader = (role) => normalizeRole(role) === 'teamLeader';

/**
 * Check if role is a regular employee.
 * @param {string} role - User role
 * @returns {boolean}
 */
export const isEmployee = (role) => normalizeRole(role) === 'employee';

/**
 * Get normalized role from user object
 * @param {Object} user - User object
 * @returns {string|null}
 */
export const getUserRole = (user) => {
  if (!user) return null;
  return normalizeRole(user.role);
};

/**
 * Resolve the backend user id from a stored user object.
 * Login historically persisted `userId` while some pages read `id`.
 * @param {Object} user
 * @returns {string|null}
 */
export const getUserId = (user) => {
  if (!user) return null;
  return user.userId || user.id || user._id || null;
};
